import { Injectable, computed, signal } from '@angular/core';
import { MemberCampaign } from './members.service';

/**
 * Shared, read-only state for the member currently open in the member-detail layout.
 *
 * Tracks whether the member is "Merged". Merged members are view-only: every edit
 * affordance across the tabs is gated on `!isMerged()`. The flag defaults to false and is set by the
 * member layout once the member loads, so non-merged (active/inactive) members are completely
 * unaffected — `!isMerged()` is always true for them.
 *
 * Also holds the campaigns the member belongs to and the one picked in the header's campaign
 * selector, which Receipt Submissions and the manual stamp / points dialogs work against.
 */
@Injectable({ providedIn: 'root' })
export class MemberContextService {
  private readonly _isMerged = signal(false);
  private readonly _isEmailVerified = signal(false);
  private readonly _campaigns = signal<MemberCampaign[]>([]);
  private readonly _campaignsLoading = signal(false);
  // Keyed by Campaigns.Id: the GUID isn't unique (a cloned campaign can share it).
  private readonly _selectedCampaignId = signal<number | null>(null);

  /** True only when the currently loaded member's activity status is 'Merged'. */
  readonly isMerged = this._isMerged.asReadonly();

  /**
   * True when the currently loaded member's email address is verified. Set by the member layout
   * (which fetches the verification flag) so child tabs like the dashboard can show the "Verified"
   * badge without re-fetching or depending on the dashboard endpoint returning it.
   */
  readonly isEmailVerified = this._isEmailVerified.asReadonly();

  /** Campaigns the current member belongs to, in API order. */
  readonly campaigns = this._campaigns.asReadonly();

  /** True while the layout is fetching the member's campaigns. */
  readonly campaignsLoading = this._campaignsLoading.asReadonly();

  /** The campaign picked in the header; null when the member has no campaigns. */
  readonly selectedCampaign = computed<MemberCampaign | null>(() => {
    const id = this._selectedCampaignId();
    return this._campaigns().find((c) => c.campaignId === id) ?? null;
  });

  /** Set by the member layout after the member's status is known. */
  setMerged(isMerged: boolean): void {
    this._isMerged.set(isMerged);
  }

  /** Set by the member layout once the email-verified flag is known (and when it changes). */
  setEmailVerified(isEmailVerified: boolean): void {
    this._isEmailVerified.set(isEmailVerified);
  }

  setCampaignsLoading(loading: boolean): void {
    this._campaignsLoading.set(loading);
  }

  /** Replaces the member's campaigns and preselects the first one. */
  setCampaigns(campaigns: MemberCampaign[]): void {
    this._campaigns.set(campaigns);
    this._selectedCampaignId.set(campaigns[0]?.campaignId ?? null);
  }

  /** app-dropdown hands the id back as a string, so normalize it to the number the lookup uses. */
  selectCampaign(campaignId: number | string | null): void {
    this._selectedCampaignId.set(campaignId === null || campaignId === '' ? null : Number(campaignId));
  }

  /** Reset to the safe defaults when switching members or leaving the detail view. */
  reset(): void {
    this._isMerged.set(false);
    this._isEmailVerified.set(false);
    this._campaigns.set([]);
    this._campaignsLoading.set(false);
    this._selectedCampaignId.set(null);
  }
}
